import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, User, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const Avatar = ({ path, size = 32 }) => {
  const src = path ? `${BACKEND}/api/files/${path}` : "";
  if (!src) {
    return (
      <div style={{ width: size, height: size }} className="rounded-full bg-muted ring-1 ring-secondary/30 flex items-center justify-center shrink-0">
        <User size={size * 0.5} className="text-muted-foreground" />
      </div>
    );
  }
  return <img src={src} alt="" style={{ width: size, height: size }} className="rounded-full object-cover ring-1 ring-secondary/30 shrink-0" />;
};

export default function NotificationsBell({ target = "/portal" }) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const timerRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadCount = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/community/notifications/unread-count`, auth);
      setUnread(data.unread || 0);
    } catch { /* */ }
    // eslint-disable-next-line
  }, [token]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/community/notifications`, auth);
      setItems(data);
    } catch { /* */ }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadCount();
    timerRef.current = setInterval(loadCount, 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [token, loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const click = async (n) => {
    try { await axios.post(`${API}/community/notifications/${n.id}/read`, {}, auth); } catch { /* */ }
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setItems((old) => old.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setOpen(false);
    // Route to community tab with highlight
    const dest = user?.is_admin ? "/admin" : target;
    navigate(`${dest}?tab=community&highlight=${n.post_id}${n.comment_id ? `&comment=${n.comment_id}` : ""}`);
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/community/notifications/read-all`, {}, auth);
      setUnread(0);
      setItems((old) => old.map((x) => ({ ...x, read: true })));
    } catch { /* */ }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notifications-bell-wrap">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        className="relative w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-secondary transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center px-1 font-body"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-11 w-[360px] max-h-[520px] overflow-y-auto bg-black border border-secondary/40 shadow-2xl z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-black">
            <p className="overline text-secondary text-[0.6rem]">Notifications</p>
            <div className="flex items-center gap-3">
              {items.some((x) => !x.read) && (
                <button onClick={markAll} className="overline text-[0.55rem] text-primary hover:text-foreground" data-testid="notifications-read-all">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-primary">
                <X size={14} />
              </button>
            </div>
          </div>
          {items.length === 0 && (
            <p className="p-8 font-editorial italic text-center text-muted-foreground text-sm" data-testid="notifications-empty">
              You have no notifications yet.
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => click(n)}
              data-testid={`notification-${n.id}`}
              className={`w-full flex items-start gap-3 px-4 py-3 border-b border-border text-left hover:bg-muted/30 transition-colors ${n.read ? "" : "bg-primary/5"}`}
            >
              <Avatar path={n.actor_avatar_path} size={34} />
              <div className="flex-1 min-w-0">
                <p className="font-body text-xs text-muted-foreground">
                  <span className="text-foreground">{n.actor_name}</span> mentioned you {n.type === "comment" ? "in a comment" : "in a post"} · {timeAgo(n.created_at)} ago
                </p>
                <p className="font-body text-sm text-foreground mt-1 line-clamp-2">{n.snippet}</p>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" data-testid="notif-unread-dot" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
